<template>
  <div>
    <b-card>
      <h4 class="mb-2">
        Uczestnicy i wyniki
      </h4>

      <b-table
        ref="refCertificationSlotsTable"
        class="position-relative"
        :items="slots"
        responsive
        :fields="visibleTableColumns"
        primary-key="id"
        show-empty
        empty-text="Brak uczestników."
        bordered
      >
        <template #cell(time)="{ item }">
          {{ item.time | time }}
        </template>

        <template #cell(user)="{ item }">
          <span v-if="item.user">{{ item.user.firstName }} {{ item.user.lastName }}</span>
          <span
            v-else
            class="text-muted"
          >-</span>
        </template>

        <template #cell(userEmail)="{ item }">
          <span v-if="item.user && item.user.email">{{ item.user.email }}</span>
          <span
            v-else
            class="text-muted"
          >-</span>
        </template>

        <template #cell(userDealerName)="{ item }">
          <span v-if="item.user && item.user.dealerName">{{ item.user.dealerName }}</span>
          <span
            v-else
            class="text-muted"
          >-</span>
        </template>

        <template #cell(attendance)="{ item }">
          <span v-if="item.attendance === true">Tak</span>
          <span v-else-if="item.attendance === false">Nie</span>
          <span v-else>-</span>
        </template>

        <template #cell(passed)="{ item }">
          <b-badge
            v-if="item.passed === true"
            variant="light-success"
          >
            Zaliczone
          </b-badge>
          <b-badge
            v-else-if="item.passed === false"
            variant="light-danger"
          >
            Niezaliczone
          </b-badge>
          <span v-else>-</span>
        </template>

        <template #cell(justification)="{ item }">
          <span v-if="item.justification">{{ item.justification }}</span>
          <span v-else>-</span>
        </template>

        <template #cell(validUntil)="{ item }">
          <span v-if="item.infinite">Bezterminowo</span>
          <span v-else-if="item.validUntil">{{ item.validUntil | date }}</span>
          <span v-else>-</span>
        </template>

        <template #cell(actions)="{ item }">
          <b-button-group v-if="item.enrolmentId">
            <b-button
              v-if="isAdmin || isTrainer"
              size="sm"
              variant="light"
              @click="openResultModal(item)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">Wynik</span>
            </b-button>
            <b-button
              v-if="item.resultFile"
              size="sm"
              variant="light"
              @click="downloadResultFile(item)"
            >
              <feather-icon icon="DownloadIcon" />
              <span class="align-middle ml-50">Pobierz plik</span>
            </b-button>
            <span
              v-if="item.passed === true"
              v-b-tooltip.hover.top="certificatePdfDownloadEnabled ? '' : 'Certyfikat nie jest dostępny'"
              tabindex="0"
              class="cert-btn-group-item"
            >
              <b-button
                size="sm"
                variant="light"
                class="rounded-0 h-100"
                :disabled="!certificatePdfDownloadEnabled"
                @click="downloadCertificate(item)"
              >
                <feather-icon icon="DownloadIcon" />
                <span class="align-middle ml-50">Certyfikat</span>
              </b-button>
            </span>
            <b-button
              v-if="isAdmin"
              size="sm"
              variant="light"
              :disabled="!canDeleteCertificationEnrolment(item)"
              @click="enrolmentToDelete = item"
            >
              <feather-icon icon="Trash2Icon" />
              <span class="align-middle ml-50">Usuń</span>
            </b-button>
          </b-button-group>
        </template>
      </b-table>
    </b-card>

    <b-modal
      v-if="resultSlot"
      v-model="resultModalOpen"
      centered
      title="Wynik certyfikacji"
      ok-title="Zapisz"
      cancel-title="Anuluj"
      @ok.prevent="saveResult"
    >
      <b-form-group label="Obecność">
        <v-select
          v-model="resultForm.attendance"
          :options="yesNoOptions"
          :reduce="val => val.value"
          :clearable="false"
        />
      </b-form-group>
      <b-form-group label="Wynik">
        <v-select
          v-model="resultForm.passed"
          :options="resultOptions"
          :reduce="val => val.value"
          :clearable="false"
        />
      </b-form-group>
      <b-form-group label="Uzasadnienie">
        <b-form-textarea
          v-model="resultForm.justification"
          rows="3"
        />
      </b-form-group>
      <b-form-group label="Plik">
        <div
          v-if="resultFileDisplayName"
          class="d-flex align-items-center flex-wrap"
        >
          <b-button
            variant="light"
            size="sm"
            @click="previewCurrentResultFile"
          >
            <feather-icon
              icon="FileIcon"
              class="mr-25"
            />
            {{ resultFileDisplayName }}
          </b-button>
          <b-button
            variant="outline-primary"
            size="sm"
            class="ml-50"
            @click="$refs.resultFileInput.click()"
          >
            Zmień plik
          </b-button>
          <b-button
            variant="outline-danger"
            size="sm"
            class="ml-50"
            @click="removeResultFile"
          >
            Usuń plik
          </b-button>
        </div>
        <b-button
          v-else
          variant="outline-primary"
          size="sm"
          @click="$refs.resultFileInput.click()"
        >
          <feather-icon icon="PlusIcon" />
          Dodaj plik
        </b-button>
        <input
          ref="resultFileInput"
          type="file"
          style="display: none"
          :accept="resultFileAccept"
          @input="onResultFileSelected"
        >
      </b-form-group>
    </b-modal>

    <b-modal
      v-if="enrolmentToDelete"
      v-model="deleteModalOpen"
      centered
      modal-class="modal-danger"
      title="Potwierdź usunięcie"
      ok-variant="danger"
      cancel-variant="light"
      ok-title="Usuń"
      cancel-title="Anuluj"
      @ok="deleteEnrolment"
    >
      Na pewno usunąć zapis uczestnika z tego slotu?
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BTable,
  BBadge,
  BButtonGroup,
  BModal,
  BFormGroup,
  BFormTextarea,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import visibleTableColumns from '@/helpers/visibleTableColumns'
import {
  buildCertificationDownloadFilename,
  buildCertificationResultForm,
  buildMediaObjectDownloadUrl,
  buildSetAttendancePayload,
  canDeleteCertificationEnrolment,
  CERTIFICATE_PDF_DOWNLOAD_ENABLED,
  CERTIFICATION_RESULT_FILE_ACCEPT,
  getCertificationResultFileExtension,
  parseContentDispositionFilename,
  triggerBlobDownload,
} from '@/helpers/certification/certificationEnrolmentsLogic'
import { filterCertificationEnrolmentSlots } from '@/helpers/certification/certificationDetailsLogic'

export default {
  components: {
    BCard,
    BButton,
    BTable,
    BBadge,
    BButtonGroup,
    BModal,
    BFormGroup,
    BFormTextarea,
    vSelect,
  },
  props: {
    certificationData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      tableColumns: [
        { key: 'time', label: 'Godzina' },
        { key: 'user', label: 'Imię i nazwisko' },
        { key: 'userEmail', label: 'Email' },
        { key: 'userDealerName', label: 'Dealer' },
        { key: 'attendance', label: 'Obecność', visibleTo: ['ROLE_ADMIN', 'ROLE_FORD_POLAND', 'ROLE_TRAINER'] },
        { key: 'passed', label: 'Wynik' },
        { key: 'justification', label: 'Uzasadnienie' },
        { key: 'validUntil', label: 'Ważność' },
        { key: 'actions', label: 'Akcje' },
      ],
      resultSlot: null,
      resultForm: {
        attendance: true,
        passed: true,
        justification: null,
      },
      yesNoOptions: [
        { label: 'Tak', value: true },
        { label: 'Nie', value: false },
      ],
      resultOptions: [
        { label: 'Zaliczone', value: true },
        { label: 'Niezaliczone', value: false },
      ],
      enrolmentToDelete: null,
      resultFileAccept: CERTIFICATION_RESULT_FILE_ACCEPT,
      certificatePdfDownloadEnabled: CERTIFICATE_PDF_DOWNLOAD_ENABLED,
    }
  },
  computed: {
    slots() {
      return filterCertificationEnrolmentSlots(this.certificationData.slots, {
        isAdmin: this.isAdmin,
        isFordPoland: this.isFordPoland,
        isTrainer: this.isTrainer,
      })
    },

    visibleTableColumns,

    resultFileDisplayName() {
      if (this.resultForm.pendingFile) {
        return this.resultForm.pendingFile.name
      }
      if (this.resultForm.resultFile) {
        return this.resultForm.resultFile.originalName
      }
      return null
    },

    resultModalOpen: {
      get() {
        return !!this.resultSlot
      },
      set(value) {
        if (!value) this.resultSlot = null
      },
    },

    deleteModalOpen: {
      get() {
        return !!this.enrolmentToDelete
      },
      set(value) {
        this.enrolmentToDelete = value
      },
    },
  },
  methods: {
    openResultModal(slot) {
      this.resultSlot = slot
      this.resultForm = buildCertificationResultForm(slot)
    },

    onResultFileSelected() {
      const input = this.$refs.resultFileInput
      if (!input) return

      const file = input.files[0]
      if (file) {
        this.resultForm.pendingFile = file
        this.resultForm.removeResultFile = false
      }
      input.value = ''
    },

    uploadResultFile(file) {
      const formdata = new FormData()
      formdata.append('file', file)
      return this.$http.post('media_objects', formdata).then(response => {
        if (response && response.status === 201) {
          return response.data['@id']
        }
        throw response
      })
    },

    previewResultFile(file) {
      window.open(buildMediaObjectDownloadUrl(this.$http.defaults.baseURL, file.contentUrl))
    },

    previewPendingResultFile() {
      if (!this.resultForm.pendingFile) return
      const objectUrl = URL.createObjectURL(this.resultForm.pendingFile)
      window.open(objectUrl)
    },

    previewCurrentResultFile() {
      if (this.resultForm.pendingFile) {
        this.previewPendingResultFile()
      } else if (this.resultForm.resultFile) {
        this.previewResultFile(this.resultForm.resultFile)
      }
    },

    removeResultFile() {
      if (this.resultForm.pendingFile) {
        this.resultForm.pendingFile = null
      } else if (this.resultForm.resultFile) {
        this.resultForm.resultFile = null
        this.resultForm.removeResultFile = true
      }

      const input = this.$refs.resultFileInput
      if (input) {
        input.value = ''
      }
    },

    downloadResultFile(slot) {
      if (!slot.resultFile) return

      this.$store.dispatch('certification/downloadResultFile', {
        id: slot.enrolmentId,
        mediaObjectId: slot.resultFile.id,
      })
        .then(response => {
          if (!response || response.status !== 200) return

          const filename = parseContentDispositionFilename(response.headers['content-disposition'])
            || slot.resultFile.originalName
            || buildCertificationDownloadFilename(
              slot.user,
              getCertificationResultFileExtension(slot.resultFile.originalName),
            )

          triggerBlobDownload(new Blob([response.data], { type: 'application/octet-stream' }), filename)
        })
    },

    saveResult() {
      const uploadPromise = this.resultForm.pendingFile
        ? this.uploadResultFile(this.resultForm.pendingFile)
        : Promise.resolve(null)

      uploadPromise
        .then(resultFileIri => {
          const form = { ...this.resultForm }
          if (resultFileIri) {
            form.resultFileIri = resultFileIri
          }
          return this.$store.dispatch('certification/setAttendance', buildSetAttendancePayload(
            this.certificationData.id,
            this.resultSlot.enrolmentId,
            form,
          ))
        })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Zapisano wynik certyfikacji',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.resultSlot = null
          this.$emit('refetch-data')
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Nie udało się zapisać wyniku.',
              text: error && error.response && error.response.data ? error.response.data['hydra:description'] : null,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
            timeout: false,
          })
        })
    },

    deleteEnrolment() {
      this.$store.dispatch('certification/deleteEnrolment', { id: this.enrolmentToDelete.enrolmentId })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Usunięto zapis na certyfikację',
              icon: 'UserXIcon',
              variant: 'success',
            },
          })
          this.$emit('refetch-data')
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Nie udało się usunąć zapisu.',
              text: error && error.response && error.response.data ? error.response.data['hydra:description'] : null,
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
            timeout: false,
          })
        })
    },

    downloadCertificate(slot) {
      this.$store.dispatch('certification/downloadCertificate', { id: slot.enrolmentId })
        .then(response => {
          if (!response || response.status !== 200) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Nie udało się pobrać certyfikatu.',
                icon: 'AlertCircleIcon',
                variant: 'danger',
              },
              timeout: false,
            })
            return
          }

          const filename = parseContentDispositionFilename(response.headers['content-disposition'])
            || buildCertificationDownloadFilename(slot.user, 'pdf')

          triggerBlobDownload(
            new Blob([response.data], { type: 'application/pdf' }),
            filename,
          )
        })
    },

    canDeleteCertificationEnrolment,
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.btn-group > .cert-btn-group-item {
  display: flex;
  align-self: stretch;

  &:not(:first-child) > .btn {
    margin-left: -1px;
  }

  > .btn {
    height: 100%;
  }
}
</style>
